import React from 'react';
import { Zap, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button';
import { SectionWrapper } from '../SectionWrapper';
import { GradientTitle } from '../GradientTitle';
import { usePayment } from '../../hooks/usePayment';
import { PRICES, STARS_AMOUNT, MONTHLY_PRICE, calculateSavings, type Period } from '../../constants/pricing';
import { PeriodSelector } from './PeriodSelector';
import { PaymentMethodSelector } from './PaymentMethodSelector';
import { gradientTextStyle } from '../../lib/gradients';

type PaymentMethod = 'stars' | 'crypto';

interface PremiumSectionProps {
  onSuccess?: () => void;
}

export const PremiumSection: React.FC<PremiumSectionProps> = ({ onSuccess }) => {
  const { t } = useTranslation();
  const [period, setPeriod] = React.useState<Period>('3');
  const [paymentMethod, setPaymentMethod] = React.useState<PaymentMethod>('stars');

  const { isProcessing, handleSubscribe } = usePayment({
    period,
    paymentMethod,
    onSuccess
  });

  const currentPrice = PRICES[period];
  const originalPrice = +(MONTHLY_PRICE * parseInt(period)).toFixed(2);
  const savings = calculateSavings(period);

  const handleToggle = (newPeriod: Period) => {
    setPeriod(newPeriod);
  };

  const handlePaymentMethodToggle = (method: PaymentMethod) => {
    setPaymentMethod(method);
  };

  const features = [
    t('PROFILE_SCREEN.FEATURES.EARLY_ACCESS'),
    t('PROFILE_SCREEN.FEATURES.EXCLUSIVE_DEFI'),
    t('PROFILE_SCREEN.FEATURES.NOTIFICATIONS'),
    t('PROFILE_SCREEN.FEATURES.GUIDES'),
    t('PROFILE_SCREEN.FEATURES.INSIGHTS')
  ];

  return (
    <SectionWrapper>
      <GradientTitle
        title={t('PROFILE_SCREEN.PREMIUM.TITLE')}
        caption={t('PROFILE_SCREEN.PREMIUM.CAPTION')}
      />

      <div className="flex flex-col gap-5 mt-5">
        {/* Period Selection */}
        <div className="flex flex-col gap-3">
          <PeriodSelector
            period={period}
            onPeriodChange={handleToggle}
            showBestValue={true}
          />
        </div>

        {/* Price Display */}
        <div className="text-center p-4 bg-tg-secondary-bg rounded-2xl border border-pink-500/10 flex flex-col items-center gap-3">
          <div className="flex items-baseline justify-center gap-2">
            {period !== '3' && (
              <span className="text-xl font-medium text-tg-hint line-through">${originalPrice}</span>
            )}
            <span 
              className="text-[36px] font-bold max-[360px]:text-3xl"
              style={{
                ...gradientTextStyle,
                letterSpacing: '-1px'
              }}
            >
              ${currentPrice}
            </span>
            <span className="text-sm text-tg-hint font-medium">/{period} {t('PROFILE_SCREEN.PREMIUM.MONTHS')}</span>
          </div>
          
          {paymentMethod === 'stars' ? (
            <div className="inline-flex items-center justify-center gap-2 text-base font-semibold text-tg-text px-4 py-2 bg-tg-bg rounded-xl border border-pink-500/10 shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
              <span>⭐</span>
              <span>{STARS_AMOUNT[period]} Stars</span>
            </div>
          ) : null}
          
          {period !== '3' && (
            <div className="inline-flex items-center justify-center gap-1.5 text-sm text-[#FF0080] font-semibold px-3 py-1.5 rounded-lg border border-pink-500/10 bg-pink-500/5">
              <Zap size={14} />
              {t('PROFILE_SCREEN.PREMIUM.YOU_SAVE')} ${savings}
            </div>
          )}
        </div>

        {/* Payment Method Selection */}
        <div className="flex flex-col gap-2">
          <h2 className="text-base font-semibold text-tg-text m-0">
            {t('PROFILE_SCREEN.PREMIUM.PAYMENT_METHOD')}
          </h2>
          <PaymentMethodSelector
            paymentMethod={paymentMethod}
            onPaymentMethodChange={handlePaymentMethodToggle}
          />
        </div>

        {/* Features List */}
        <div className="flex flex-col gap-2 p-4 bg-tg-secondary-bg rounded-xl border border-pink-500/10">
          {features.map((feature, index) => (
            <div key={index} className="flex items-center gap-2 text-sm text-tg-text">
              <Check className="flex-shrink-0 text-[#FF0080]" size={16} />
              <span>{feature}</span>
            </div>
          ))}
        </div>

        {/* Subscribe Button */}
        <Button 
          onClick={handleSubscribe}
          fullWidth
          disabled={isProcessing}
        >
          {isProcessing ? t('PROFILE_SCREEN.PREMIUM.PROCESSING') : paymentMethod === 'stars' 
            ? t('PROFILE_SCREEN.PREMIUM.PAY_STARS', { amount: STARS_AMOUNT[period] })
            : t('PROFILE_SCREEN.PREMIUM.PAY_CRYPTO', { amount: currentPrice })}
        </Button>
      </div>
    </SectionWrapper>
  );
};
